import { useCallback, useEffect, useRef, useState } from 'react';
import { fmtDateTime, fmtMonDayOrdinal, GRAND_PRIZE_COUNT } from '../campaign';
import { Crop, BgRot, StatBox, makeSparks } from './Shared';
import { gemsChipFor, chestsChipFor } from './Hero';
import { BackIcon, LockBtnIcon, RewardIcon } from './Icons';

/* ------------------------------------------------------------------
   "Your rewards" — full-screen page over the hub with its own scroller.
   Gems tab: reserved / ready-to-open cells and the opened list; tapping
   Open calls onOpen(gemId) (the API), shakes the gem, reveals the reward
   with a spark burst, then fades the cell out so it reappears under
   "Opened". Chests tab: every Gold Chest and its draw status.
   `gems` / `chests` are the mapped wallet lists; the parent updates them
   when onOpen resolves — this page only animates the change.
   ------------------------------------------------------------------ */

const REVEAL_MS = 2600; // how long the revealed reward stays before the cell fades
const GONE_MS = 420;    // the fade itself

const STATUS_BY_KIND = { GOLD: 'Credited', SATS: 'Credited', CASHBACK: 'Credited', VOUCHER: 'Saved', BOOST: 'Active', PENDING: 'Pending' };

/* position of `el` inside the scroller's content, in stage px */
function stagePos(el, scroller) {
  let x = 0; let y = 0; let n = el;
  while (n && n !== scroller) { x += n.offsetLeft; y += n.offsetTop; n = n.offsetParent; }
  return { x, y: y - scroller.scrollTop };
}

export default function Rewards({
  phase, tab, onTab, gems, chests, grandTotal = GRAND_PRIZE_COUNT, drawDay = '13 November', launch, drawOn,
  zoom = 1, stageHeight = 812, onBack, onOpen, onError,
}) {
  const [opening, setOpening] = useState({});   // id → true while the API call / shake runs
  const [revealed, setRevealed] = useState({}); // id → { reward, at, gone } while the reveal plays
  const [sparks, setSparks] = useState([]);
  const scrollRef = useRef(null);
  const cellRefs = useRef({});
  const timers = useRef([]);
  const alive = useRef(true);

  useEffect(() => {
    const pending = timers.current;
    return () => { alive.current = false; pending.forEach(clearTimeout); };
  }, []);
  const later = useCallback((fn, ms) => { timers.current.push(setTimeout(fn, ms)); }, []);

  const open = useCallback((gem) => {
    if (opening[gem.id] || revealed[gem.id]) return;
    setOpening((o) => ({ ...o, [gem.id]: true }));
    Promise.resolve()
      .then(() => onOpen(gem.id))
      .then(({ reward, at }) => {
        if (!alive.current) return;
        const cell = cellRefs.current[gem.id];
        const scroller = scrollRef.current;
        if (cell && scroller) {
          const p = stagePos(cell, scroller);
          setSparks(makeSparks(p.x + cell.offsetWidth / 2, p.y + 42, 26));
          later(() => setSparks([]), 1300);
        }
        setOpening((o) => { const n = { ...o }; delete n[gem.id]; return n; });
        setRevealed((r) => ({ ...r, [gem.id]: { reward, at, gone: false } }));
        later(() => {
          setRevealed((r) => (r[gem.id] ? { ...r, [gem.id]: { ...r[gem.id], gone: true } } : r));
          later(() => setRevealed((r) => { const n = { ...r }; delete n[gem.id]; return n; }), GONE_MS);
        }, REVEAL_MS);
      })
      .catch((err) => {
        if (!alive.current) return;
        setOpening((o) => { const n = { ...o }; delete n[gem.id]; return n; });
        if (onError) onError(err);
      });
  }, [opening, revealed, onOpen, onError, later]);

  const pre = phase === 'pre';
  const ended = phase === 'ended';
  /* a gem stays in the grid while its reveal plays, even though it's already opened */
  const ready = gems.filter((g) => !g.opened || revealed[g.id]);
  const opened = gems.filter((g) => g.opened && !revealed[g.id]).sort((a, b) => b.opened.at - a.opened.at);
  const unopened = gems.filter((g) => !g.opened).length;
  const gemsChip = gemsChipFor(phase, gems.length, unopened, { launchLabel: launch ? fmtMonDayOrdinal(launch) : 'Oct 18th' });
  const chestsChip = chestsChipFor(phase, chests.length, { drawLabel: drawOn ? fmtMonDayOrdinal(drawOn) : 'Nov 13th' });

  return (
    <div className="gq-overlay gq-scrollable">
      <div className="gq-rewards" style={{ zoom, height: stageHeight }}>
        <BgRot />
        <div className="gq-rsparks">
          {sparks.map((s) => <i key={s.id} className="gq-spark" style={s.style} />)}
        </div>

        <div className="gq-scroll" ref={scrollRef}>
          <div className="gq-hdr">
            <button type="button" className="gq-back" onClick={onBack} aria-label="Back"><BackIcon /></button>
            <div className="gq-ttl">Your rewards</div>
          </div>
          <div className="gq-sum">
            <StatBox kind="gems" num={gems.length} label="Gems" chip={gemsChip} />
            <StatBox kind="chests" num={chests.length} label="Chests" chip={chestsChip} />
          </div>
          <div className="gq-seg" role="tablist">
            {['gems', 'chests'].map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'gq-on' : ''} onClick={() => onTab(t)}>
                {t === 'gems' ? 'Gems' : 'Chests'}
              </button>
            ))}
          </div>

          {tab === 'gems' ? (
            <>
              {!ready.length && !opened.length && (
                <div className="gq-rempty">No Gems yet. Spend ₹500 on your card, buy a voucher or gold, or refer a friend to earn your first.</div>
              )}
              {ready.length > 0 && (
                <>
                  <div className="gq-rsec">{pre ? 'Reserved' : 'Ready to open'}<span>{ready.length}</span></div>
                  <div className="gq-ggrid">
                    {ready.map((g, i) => {
                      const rv = revealed[g.id];
                      const cls = ['gq-gcell', pre ? 'gq-locked' : '', opening[g.id] ? 'gq-opening' : '', rv ? 'gq-revealed' : '', rv && rv.gone ? 'gq-gone' : '']
                        .filter(Boolean).join(' ');
                      return (
                        <div key={g.id} className={cls} style={{ '--i': i }} ref={(el) => { cellRefs.current[g.id] = el; }}>
                          <Crop kind="gem" />
                          <div className="gq-src">{g.sourceLabel}</div>
                          <div className="gq-dt">{pre ? 'Unlocks 18 Oct' : fmtDateTime(g.earnedAt)}</div>
                          <button type="button" className="gq-open" disabled={pre || !!opening[g.id] || !!rv} onClick={() => open(g)} aria-busy={!!opening[g.id]}>
                            {pre ? <><LockBtnIcon width="12" height="14" />Locked</> : 'Open'}
                          </button>
                          <div className="gq-rv" aria-live="polite">
                            <div className="gq-ic">{rv && <RewardIcon kind={rv.reward.kind} />}</div>
                            <div className="gq-rn">{rv ? rv.reward.name : ''}</div>
                            <div className="gq-rs">{rv ? `${rv.reward.detail} · ${fmtDateTime(rv.at)}` : ''}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {opened.length > 0 && (
                <>
                  <div className="gq-rsec">Opened<span>{opened.length}</span></div>
                  <div className="gq-rlist">
                    {opened.map((g, i) => (
                      <div className="gq-rrow" key={g.id} style={{ '--i': i }}>
                        <div className="gq-ic"><RewardIcon kind={g.opened.reward.kind} /></div>
                        <div>
                          <div className="gq-rn">{g.opened.reward.name}</div>
                          <div className="gq-rs">{g.sourceLabel} · {fmtDateTime(g.opened.at)}</div>
                        </div>
                        <div className="gq-st">{STATUS_BY_KIND[g.opened.reward.kind] || 'Credited'}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {!chests.length ? (
                <div className="gq-rempty">
                  No chests yet. Complete all four steps of any streak to earn a Gold Chest. Every chest wins, and {grandTotal} grand prizes are drawn on {drawDay}.
                </div>
              ) : (
                <>
                  <div className="gq-rsec">Your chests<span>{chests.length} in the draw</span></div>
                  <div className="gq-rlist">
                    {chests.map((c, i) => {
                      const grand = ended && c.result && c.result.kind === 'GRAND';
                      let status = 'In the draw';
                      if (ended) status = c.result ? `Won · ${c.result.prize || c.result.kind}` : 'Drawn';
                      return (
                        <div className="gq-rrow" key={c.id} style={{ '--i': i }}>
                          <div className="gq-ic gq-ch"><Crop kind="chest" /></div>
                          <div>
                            <div className="gq-rn">{c.streakName} streak chest</div>
                            <div className="gq-rs">Earned {fmtDateTime(c.earnedAt)}</div>
                          </div>
                          <div className={`gq-st${grand ? ' gq-gold' : ''}`}>{status}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="gq-note">
                    {ended
                      ? `Winners were drawn on ${drawDay}. Delivery details go out by SMS.`
                      : `Every chest wins. ${grandTotal} grand prizes are drawn at random on ${drawDay}; every other chest wins Sats or Gold.`}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
